import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchGuestTabletSessionStaff,
  guestTabletEventsUrl,
} from "@/lib/guestTabletApi";
import { mergeGuestTabletSession } from "@/lib/guestTabletSessionMerge";
import type { GuestTabletPendingPayment, GuestTabletSessionDisplay } from "@/types/guestTablet";

const SSE_RECONNECT_MS = 3_000;
/** Schnelles Polling bei getrennter SSE-Verbindung */
const FAST_POLL_MS = 1_000;
/** Sicherheits-Polling bei aktiver SSE (Fallback wenn Events fehlen) */
const SAFETY_POLL_MS = 3_000;

export type GuestTabletStaffSessionState = {
  session: GuestTabletSessionDisplay | null;
  pendingPayment: GuestTabletPendingPayment | null;
  guestDataConfirmed: boolean;
  assignedPitchId: string | null;
  loading: boolean;
  reload: () => Promise<void>;
};

/**
 * Live-Session für die Rezeption (Check-in): SSE wie am Tablet, Fallback-Polling.
 */
export function useGuestTabletStaffSession(
  workstationCode: string | null | undefined,
  options?: { enabled?: boolean },
): GuestTabletStaffSessionState {
  const enabled = Boolean(options?.enabled !== false && workstationCode);
  const code = workstationCode ?? "";

  const [session, setSession] = useState<GuestTabletSessionDisplay | null>(null);
  const [loading, setLoading] = useState(false);
  const reloadInFlightRef = useRef<Promise<void> | null>(null);

  const applySession = useCallback((incoming: GuestTabletSessionDisplay) => {
    setSession((prev) => mergeGuestTabletSession(prev, incoming));
    setLoading(false);
  }, []);

  const reload = useCallback(async () => {
    if (!enabled || !code) return;
    if (reloadInFlightRef.current) {
      return reloadInFlightRef.current;
    }
    const run = (async () => {
      try {
        const fresh = await fetchGuestTabletSessionStaff(code);
        applySession(fresh);
      } catch {
        /* optional – Tablet-Sync darf Check-in nicht blockieren */
      } finally {
        setLoading(false);
      }
    })();
    reloadInFlightRef.current = run;
    try {
      await run;
    } finally {
      reloadInFlightRef.current = null;
    }
  }, [enabled, code, applySession]);

  useEffect(() => {
    if (!enabled) {
      setSession(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    void reload();
  }, [enabled, code, reload]);

  useEffect(() => {
    if (!enabled || !code) return;

    let disposed = false;
    let eventSource: EventSource | null = null;
    let reconnectTimer: number | null = null;
    let fastPollTimer: number | null = null;
    let safetyPollTimer: number | null = null;

    const clearReconnect = () => {
      if (reconnectTimer != null) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const stopFastPoll = () => {
      if (fastPollTimer != null) {
        window.clearInterval(fastPollTimer);
        fastPollTimer = null;
      }
    };

    const startFastPoll = () => {
      if (disposed || fastPollTimer != null) return;
      fastPollTimer = window.setInterval(() => {
        void reload();
      }, FAST_POLL_MS);
    };

    const stopSafetyPoll = () => {
      if (safetyPollTimer != null) {
        window.clearInterval(safetyPollTimer);
        safetyPollTimer = null;
      }
    };

    const startSafetyPoll = () => {
      if (disposed || safetyPollTimer != null) return;
      safetyPollTimer = window.setInterval(() => {
        void reload();
      }, SAFETY_POLL_MS);
    };

    const scheduleReconnect = () => {
      if (disposed || reconnectTimer != null) return;
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, SSE_RECONNECT_MS);
    };

    const connect = () => {
      if (disposed) return;
      eventSource?.close();
      eventSource = null;

      try {
        eventSource = new EventSource(guestTabletEventsUrl(code));
      } catch {
        startFastPoll();
        scheduleReconnect();
        return;
      }

      eventSource.addEventListener("guest_tablet_session_changed", (ev) => {
        try {
          const parsed = JSON.parse((ev as MessageEvent).data) as GuestTabletSessionDisplay;
          applySession(parsed);
        } catch {
          void reload();
        }
      });

      eventSource.onopen = () => {
        stopFastPoll();
        startSafetyPoll();
        clearReconnect();
        void reload();
      };

      eventSource.onerror = () => {
        eventSource?.close();
        eventSource = null;
        stopSafetyPoll();
        startFastPoll();
        scheduleReconnect();
      };
    };

    connect();
    startFastPoll();

    return () => {
      disposed = true;
      eventSource?.close();
      clearReconnect();
      stopFastPoll();
      stopSafetyPoll();
    };
  }, [enabled, code, reload, applySession]);

  const pendingPayment = session?.pending_payment ?? null;
  const guestDataConfirmed = Boolean(session?.pitch_selection?.guest_data_confirmed);
  const assignedPitchId = session?.pitch_selection?.assigned_pitch_id ?? null;

  return {
    session,
    pendingPayment,
    guestDataConfirmed,
    assignedPitchId,
    loading,
    reload,
  };
}
